import { zodResolver } from "@hookform/resolvers/zod";
import {
	OTP_RESEND_COOLDOWN_SECONDS,
	type RequestOtpInput,
	requestOtpSchema,
	UserRole,
	type VerifyOtpInput,
	verifyOtpSchema,
} from "@storyline/shared";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router";
import { AuthError, requestOtp, verifyOtp } from "@/src/auth/services/auth-http.service";
import { clearAccessDenied, denyAccess, signIn } from "@/src/common/redux/session-slice";
import { useAppDispatch } from "@/src/common/redux/store";
import { clearTokens } from "@/src/common/services/token-store.service";

type LoginStep = "email" | "code";

const GENERIC_ERROR = "La connexion a échoué. Veuillez réessayer.";
const ACCESS_DENIED_ERROR = "Ce compte n'a pas accès au back-office.";

export const useLogin = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [step, setStep] = useState<LoginStep>("email");
	const [email, setEmail] = useState("");
	const [devCode, setDevCode] = useState<string | null>(null);
	const [cooldown, setCooldown] = useState(0);
	const [error, setError] = useState<string | null>(null);

	const emailForm = useForm<RequestOtpInput>({ resolver: zodResolver(requestOtpSchema), defaultValues: { email: "" } });
	const codeForm = useForm<VerifyOtpInput>({ resolver: zodResolver(verifyOtpSchema), defaultValues: { email: "", code: "" } });

	useEffect(() => {
		if (cooldown <= 0) {
			return;
		}
		const interval = setInterval(() => setCooldown((current) => Math.max(0, current - 1)), 1000);
		return () => clearInterval(interval);
	}, [cooldown]);

	const startCooldown = useCallback(() => setCooldown(OTP_RESEND_COOLDOWN_SECONDS), []);

	const onSubmitEmail = emailForm.handleSubmit(async ({ email: value }) => {
		setError(null);
		dispatch(clearAccessDenied());
		try {
			const { devCode: nextDevCode } = await requestOtp(value);
			setEmail(value);
			codeForm.reset({ email: value, code: "" });
			setDevCode(nextDevCode);
			setStep("code");
			startCooldown();
		} catch (caught) {
			setError(caught instanceof AuthError ? caught.message : GENERIC_ERROR);
		}
	});

	const onSubmitCode = codeForm.handleSubmit(async ({ email: value, code }) => {
		setError(null);
		try {
			const user = await verifyOtp(value, code);
			if (user.role !== UserRole.ADMIN) {
				await clearTokens();
				dispatch(denyAccess());
				setError(ACCESS_DENIED_ERROR);
				setStep("email");
				return;
			}
			dispatch(signIn(user));
			const from = typeof location.state === "object" && location.state !== null && "from" in location.state ? location.state.from : null;
			navigate(typeof from === "string" && from.startsWith("/") ? from : "/", { replace: true });
		} catch (caught) {
			setError(caught instanceof AuthError ? caught.message : GENERIC_ERROR);
		}
	});

	const resend = useCallback(async () => {
		if (cooldown > 0) {
			return;
		}
		setError(null);
		try {
			const { devCode: nextDevCode } = await requestOtp(email);
			setDevCode(nextDevCode);
			startCooldown();
		} catch (caught) {
			setError(caught instanceof AuthError ? caught.message : GENERIC_ERROR);
		}
	}, [cooldown, email, startCooldown]);

	const backToEmail = useCallback(() => {
		setError(null);
		setStep("email");
	}, []);

	return {
		step,
		email,
		devCode,
		cooldown,
		error,
		emailForm,
		codeForm,
		onSubmitEmail,
		onSubmitCode,
		resend,
		backToEmail,
		isRequesting: emailForm.formState.isSubmitting,
		isVerifying: codeForm.formState.isSubmitting,
	};
};
